/**
 * Command-queue bridge. Drains the zustand command queue for any command
 * whose entityType (and targetPath, for route-filter) matches the current
 * CustomDash page. Commands the drawer emits just before `navigate()` wait
 * in the queue until the target page mounts this bridge, then fire.
 *
 * Key property: commands survive navigation. The old synchronous-handler
 * design (and its `bindingRef` predecessor) lost commands emitted before
 * the target page's bridge had registered.
 */
import mixpanel from 'mixpanel-browser';
import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';

import { useSkillTaxonomy } from '../../shared/user-preferences/hooks/useSkillTaxonomySelection';
import { convertTabContentToLLM } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { getEntityTypeFromPrimaryFilter } from '../CustomDash/components/EditableGrid/getEntityTypeFromPrimaryFilter';
import { useEditMode } from '../CustomDash/components/EditModeProvider/EditModeProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import {
  PageType,
  RevelioPageId,
} from '../CustomDash/components/PageConfigProvider/types';
import { aiFilterSearchRequest } from '../CustomDash/filters/ai-search/ai-search-api-request';
import {
  getRoleTaxonomyIdForAiSearch,
  getSkillTaxonomyIdFromActiveFilterMap,
} from '../CustomDash/filters/ai-search/getAiSearchTaxonomyFromFilterMap';
import { screenerAiSearchRequest } from '../CustomDash/filters/ai-search/screener-ai-search-api-request';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../CustomDash/filters/contexts/FilterProvider';
import {
  filterIdToEntityType,
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../CustomDash/utils/routing-helpers';
import { formatClarificationQuestionsAsText } from './format-clarification-questions';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantEntityType,
  toCustomDashAssistantEntityType,
} from './global-ai-assistant-api';
import {
  type GlobalAiCommand,
  useGlobalAiCommandStore,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import {
  type CachedPageStreamResult,
  readCachedPageStream,
  writeCachedPageStream,
} from './global-ai-assistant-page-stream-cache';
import {
  runDashboardPageStreamCreate,
  runDashboardPageStreamUpdate,
} from './global-ai-run-page-stream';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';
import { useGlobalAssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

function getPrimaryFilterForAssistantEntity(
  entityType: CustomDashAssistantEntityType
) {
  return PRIMARY_FILTER_BY_AI_ENTITY[entityType];
}

type GlobalAiBridgeCommandMatchContext = {
  editMode: boolean;
  entityTypeNow: GlobalAssistantEntityType | null;
  pathname: string;
};

export function shouldGlobalAiBridgeHandleCommand(
  command: GlobalAiCommand,
  { editMode, entityTypeNow, pathname }: GlobalAiBridgeCommandMatchContext
) {
  if (command.kind === 'runDiscoveryAiSearchAfterNavigate') {
    return globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (entityTypeNow === null || command.entityType !== entityTypeNow) {
    return false;
  }

  if (command.kind === 'applyRouteFilters') {
    return globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.kind === 'applySingleChart') {
    return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.mode === 'update') {
    // Update mode intentionally targets the currently mounted edit page.
    // Unlike draft-opening commands, it has no future destination path.
    return editMode;
  }

  return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
}

export function GlobalAIAssistantBridge() {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const ctx = useGlobalAIAssistantOptional();
  const { pathname } = useLocation();
  const {
    config: pageConfig,
    discardChanges,
    hasUnsavedChanges,
  } = usePageConfig();
  const { editMode } = useEditMode();
  const { config: tabContent, dispatch } = useTabContentConfig();
  const { activeFilterMap } = useFilterContext();
  // TODO(backend-adaptive): the two readiness hooks below + the
  // `isRoleTaxonomyResolved && isSkillTaxonomyResolved` gate in the
  // publish effect below exist to avoid publishing a stale pre-resolution
  // taxonomy into the store (which the drawer would then use for a wasted
  // fetch under the wrong cache key). Once the backend accepts "adaptive"
  // and the drawer no longer needs to mirror the adaptive-resolved value,
  // this whole readiness gate can go. See the refinement-pass docblock in
  // `global-ai-assistant-drawer.tsx` for context.
  const isRoleTaxonomyResolved = useIsTaxonomyResolved();
  // `useSkillTaxonomy` returns `undefined` while adaptive skill taxonomy
  // is still resolving; `number` (including 0 for a non-adaptive user
  // with default preference) once settled.
  const resolvedSkillTaxonomy = useSkillTaxonomy();
  const isSkillTaxonomyResolved = resolvedSkillTaxonomy !== undefined;
  // Used for the page-stream cache key: composes the bridge's live
  // rolesTax/skillsTax (from activeFilterMap) with the user's saved-pref
  // adaptive identity. Same reasoning as the filter-prefetch cache —
  // adaptive re-resolution should not fracture the cache into duplicate
  // entries. See `AssistantTaxonomy` docstring.
  const prefsTaxonomyByEntity = useGlobalAssistantTaxonomy();
  const { applyAiSearchFilters, applyScreenerAiSearchFilters } =
    useApplyAiSearchFilters();

  /**
   * Screener v2 public pages (company/geo/role Discovery) have no
   * `tabContent.primaryFilter` — the dimension is implicit from the template.
   * Without this branch `entityTypeNow` is null, the command-queue effect
   * bails, and global-assistant route filters (including screener endpoint
   * responses) never apply.
   */
  const entityTypeNow = (() => {
    if (pageConfig.type === PageType.REPORT) {
      return null;
    }
    if (pageConfig.revelioPageId === RevelioPageId.TALENT_DISCOVERY) {
      return 'talentDiscovery' as const;
    }
    const fromPrimary = getEntityTypeFromPrimaryFilter(
      tabContent.primaryFilter
    );
    if (fromPrimary) {
      return fromPrimary;
    }
    if (isScreenerV2Page(pageConfig.revelioPageId)) {
      const pf = getPrimaryFilterIdForScreenerV2Page(pageConfig.revelioPageId);
      if (pf) {
        const e = filterIdToEntityType(pf);
        if (e === 'company' || e === 'geography' || e === 'role') {
          return e;
        }
      }
    }
    return null;
  })();

  // Subscribe to the queue — bridge re-runs its drain effect whenever the
  // queue changes OR any dep that affects command handling changes.
  const queue = useGlobalAiCommandStore((state) => state.queue);
  const claim = useGlobalAiCommandStore((state) => state.claim);
  const complete = useGlobalAiCommandStore((state) => state.complete);
  const pruneExpired = useGlobalAiCommandStore((state) => state.pruneExpired);
  const publishActiveTaxonomy = useGlobalAiCommandStore(
    (state) => state.publishActiveTaxonomy
  );
  const publishLastAppliedFilterContext = useGlobalAiCommandStore(
    (state) => state.publishLastAppliedFilterContext
  );

  // Publish the current page's taxonomy into the store so the drawer (which
  // lives outside CustomDash and has no filter context) can use the ACTIVE
  // page taxonomy when resolving AI filters. Without this the drawer falls
  // back to saved preferences, which miss session-only overrides (e.g. the
  // user picking a taxonomy in Preferences without clicking "Set as default").
  //
  // Gated on BOTH role and skill taxonomy resolution to avoid publishing
  // a pre-resolution value (see the TODO(backend-adaptive) note on the
  // readiness hooks above).
  //
  // TODO(backend-adaptive): re-evaluate the entire publish effect when
  // the backend accepts "adaptive". The drawer's primary reason for
  // reading `activeTaxonomyByEntity` is the refinement pass; once that's
  // gone, this effect's remaining use is niche session-override support.
  // See the refinement-pass docblock in `global-ai-assistant-drawer.tsx`.
  useEffect(() => {
    if (!enabled || !entityTypeNow) return;
    if (!isRoleTaxonomyResolved || !isSkillTaxonomyResolved) return;
    const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
    const skillsTax = getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
    publishActiveTaxonomy(entityTypeNow, { rolesTax, skillsTax });
    return () => {
      publishActiveTaxonomy(entityTypeNow, undefined);
    };
  }, [
    enabled,
    entityTypeNow,
    activeFilterMap,
    isRoleTaxonomyResolved,
    isSkillTaxonomyResolved,
    publishActiveTaxonomy,
  ]);

  // Expose the current CustomDash UI state (edit mode + whether the page
  // already has items) to the drawer so it can include those hints in
  // backend requests (the "should we offer Update vs Create?" routing on
  // the backend keys off this). `useLayoutEffect` so it runs before the
  // drawer's next render; it's a pure ref write, no state change.
  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.customDashAssistContextRef.current = {
      isInCustomDashEditMode: editMode,
      hasCurrentPageItems: (tabContent.items?.length ?? 0) > 0,
      hasUnsavedChanges,
      discardChanges,
    };
    return () => {
      if (!ctx) return;
      ctx.customDashAssistContextRef.current = {
        isInCustomDashEditMode: false,
        hasCurrentPageItems: false,
        hasUnsavedChanges: false,
        discardChanges: undefined,
      };
    };
  }, [ctx, discardChanges, editMode, hasUnsavedChanges, tabContent.items]);

  useEffect(() => {
    if (!enabled || !ctx) {
      return;
    }

    pruneExpired();

    // Find the first queued command this bridge can handle. Only 'queued'
    // commands are eligible — 'in_flight' ones are already being handled,
    // typically by a previous render of this bridge.
    const match = queue.find(
      (q) =>
        q.status === 'queued' &&
        shouldGlobalAiBridgeHandleCommand(q.command, {
          editMode,
          entityTypeNow,
          pathname,
        })
    );

    if (!match) return;
    if (!claim(match.command.requestId)) return;

    void runCommand(match.command);

    async function runCommand(command: GlobalAiCommand) {
      // Track whether any branch set a user-visible error message. The
      // outer finally only clears the status on success — so a message
      // from a catch block survives until the user takes the next action.
      let errorStatus: string | null = null;
      try {
        if (command.kind === 'runDiscoveryAiSearchAfterNavigate') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying filters…');
          try {
            if (pageConfig.revelioPageId === RevelioPageId.TALENT_DISCOVERY) {
              const { response } = await aiFilterSearchRequest({
                prompt: command.prompt,
                rolesTax: getRoleTaxonomyIdForAiSearch(activeFilterMap),
                skillsTax:
                  getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap),
              });
              await applyAiSearchFilters(response);
            } else if (isScreenerV2Page(pageConfig.revelioPageId)) {
              const { response } = await screenerAiSearchRequest({
                prompt: command.prompt,
              });
              applyScreenerAiSearchFilters(response);
            } else {
              throw new Error(
                'Discovery filters are not supported on this page'
              );
            }
            publishLastAppliedFilterContext(command.targetPath, {
              targetPath: command.targetPath,
              surfaceKind: 'discovery',
              discoveryKind: command.discoveryKind,
              prompt: command.prompt,
            });
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED,
              {
                ...(command.discoveryKind === 'talent'
                  ? {}
                  : { entity_type: command.discoveryKind }),
                global_assistant_discovery_kind: command.discoveryKind,
              }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply filters: ${message}`;
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
              {
                ...(command.discoveryKind === 'talent'
                  ? {}
                  : { entity_type: command.discoveryKind }),
                global_assistant_discovery_kind: command.discoveryKind,
                global_assistant_route_filter_error: message,
              }
            );
          }
        } else if (command.kind === 'applyRouteFilters') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying filters…');
          // Response was resolved by the drawer in parallel with navigation,
          // so no network call here — just apply.
          try {
            if (command.filterKind === 'screener') {
              applyScreenerAiSearchFilters(command.response);
            } else {
              await applyAiSearchFilters(command.response, {
                primarySelectionMergeMode: 'replace',
              });
            }
            if (command.prompt?.trim()) {
              publishLastAppliedFilterContext(command.targetPath, {
                targetPath: command.targetPath,
                surfaceKind: 'dashboard',
                entityType: command.entityType,
                prompt: command.prompt,
              });
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply filters: ${message}`;
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
              {
                entity_type: command.entityType,
                global_assistant_route_filter_error: message,
              }
            );
          }
        } else if (command.kind === 'applySingleChart') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying chart…');
          try {
            const customDashEntityType = toCustomDashAssistantEntityType(
              command.entityType
            );
            if (!customDashEntityType) {
              throw new Error(
                'Custom Dash is not available for Individual Discovery.'
              );
            }
            const primaryFilterId =
              getPrimaryFilterForAssistantEntity(customDashEntityType);
            applyLlmTabContentToTabDispatch(
              dispatch,
              command.config,
              primaryFilterId
            );
            if (command.filters) {
              ctx.setGenerationStatus('Applying filters…');
              await applyAiSearchFilters(command.filters, {
                primarySelectionMergeMode: 'replace',
                skipTalentDiscoveryAdaptFallback: true,
              });
              if (command.prompt?.trim()) {
                publishLastAppliedFilterContext(command.targetPath, {
                  targetPath: command.targetPath,
                  surfaceKind: 'dashboard',
                  entityType: command.entityType,
                  prompt: command.prompt,
                });
              }
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_CHART_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply chart: ${message}`;
          }
        } else if (command.kind === 'startCustomPageStream') {
          if (!ctx) return;
          ctx.streamAbortRef.current?.abort();
          const ac = new AbortController();
          ctx.streamAbortRef.current = ac;
          const customDashEntityType = toCustomDashAssistantEntityType(
            command.entityType
          );
          if (!customDashEntityType) {
            ctx.setGenerationStatus(
              'Custom Dash is not available for Individual Discovery.'
            );
            ctx.streamAbortRef.current = null;
            return;
          }
          const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
          const skillsTax =
            getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);

          // Update mode: patch the current page's layout in place using
          // the bounded conversation context (recent turns + summary).
          // Deliberately skips the cache — the current tab content IS
          // the state being updated, so there's no meaningful
          // prompt+taxonomy cache key.
          if (command.mode === 'update') {
            try {
              ctx.setGenerationStatus('Updating page…');
              let sawQuestions = false;
              await runDashboardPageStreamUpdate({
                newPrompt: command.prompt,
                entityType: customDashEntityType,
                currentConfig: convertTabContentToLLM(tabContent),
                model: command.model,
                answers: command.answers,
                rolesTax,
                skillsTax,
                conversation: command.conversation,
                dispatch,
                applyAiSearchFilters,
                onEvent: (ev) => {
                  if (ev.type === 'questions') {
                    sawQuestions = true;
                    ctx.appendTranscript({
                      id: crypto.randomUUID(),
                      role: 'assistant',
                      kind: 'clarification',
                      text: formatClarificationQuestionsAsText({
                        triggerReason: ev.content.triggerReason,
                        questions: ev.content.questions,
                        emptyFallback:
                          'I need a bit more detail to update that page.',
                      }),
                    });
                    ctx.setGenerationStatus(null);
                  } else if (ev.type === 'status' || ev.type === 'error') {
                    ctx.setGenerationStatus(ev.content);
                  }
                },
                signal: ac.signal,
              });
              if (sawQuestions) {
                return;
              }
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: false,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Update failed';
            } finally {
              ctx.streamAbortRef.current = null;
            }
            return;
          }

          // Cache-key identity from saved prefs (stable across adaptive
          // re-resolution); API payload from the bridge's live filter
          // map (reflects current page state).
          const prefsTax = prefsTaxonomyByEntity(customDashEntityType);
          const cacheTaxonomy = {
            rolesTax,
            skillsTax,
            rolesKey: prefsTax.rolesKey,
            skillsKey: prefsTax.skillsKey,
          };
          const shouldUsePageStreamCache =
            !command.answers || command.answers.length === 0;

          // Fast path: if we've already generated a layout for this
          // prompt + entity + taxonomy within the cache TTL, skip the
          // SSE stream and apply the cached config + filters directly.
          const cached = shouldUsePageStreamCache
            ? readCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy
              )
            : null;
          if (cached) {
            try {
              ctx.setGenerationStatus('Reusing cached layout…');
              const primaryFilterId =
                getPrimaryFilterForAssistantEntity(customDashEntityType);
              applyLlmTabContentToTabDispatch(
                dispatch,
                cached.config,
                primaryFilterId
              );
              if (cached.filters) {
                await applyAiSearchFilters(cached.filters, {
                  primarySelectionMergeMode: 'replace',
                  skipTalentDiscoveryAdaptFallback: true,
                });
              }
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: true,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Apply failed';
            } finally {
              ctx.streamAbortRef.current = null;
            }
            return;
          }

          // Miss — run the stream, capture config + filters for the
          // cache along the way. Only write cache on successful
          // completion (no throw). Multiple config events may arrive
          // during the stream; the last one wins (matches how the
          // stream runner applies them — each call replaces the
          // previous dispatch).
          try {
            ctx.setGenerationStatus('Generating page…');
            let capturedConfig: CachedPageStreamResult['config'] | undefined;
            let capturedFilters: CachedPageStreamResult['filters'] | undefined;
            let sawQuestions = false;
            await runDashboardPageStreamCreate({
              prompt: command.prompt,
              entityType: customDashEntityType,
              model: command.model,
              answers: command.answers,
              rolesTax,
              skillsTax,
              conversation: command.conversation,
              dispatch,
              applyAiSearchFilters,
              onEvent: (ev) => {
                if (ev.type === 'config') {
                  capturedConfig = ev.content;
                } else if (ev.type === 'filters') {
                  capturedFilters = ev.content;
                } else if (ev.type === 'questions') {
                  sawQuestions = true;
                  ctx.appendTranscript({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    kind: 'clarification',
                    text: formatClarificationQuestionsAsText({
                      triggerReason: ev.content.triggerReason,
                      questions: ev.content.questions,
                      emptyFallback:
                        'I need a bit more detail to build that page.',
                    }),
                  });
                  ctx.setGenerationStatus(null);
                } else if (ev.type === 'status' || ev.type === 'error') {
                  ctx.setGenerationStatus(ev.content);
                }
              },
              signal: ac.signal,
            });
            if (sawQuestions) {
              return;
            }
            if (capturedConfig && shouldUsePageStreamCache) {
              writeCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy,
                {
                  config: capturedConfig,
                  ...(capturedFilters ? { filters: capturedFilters } : {}),
                }
              );
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
              {
                entity_type: command.entityType,
                global_assistant_page_stream_cache_hit: false,
              }
            );
          } catch (e) {
            errorStatus = e instanceof Error ? e.message : 'Generation failed';
          } finally {
            ctx.streamAbortRef.current = null;
          }
        }
      } finally {
        complete(command.requestId);
        // Only clear the status on success. If a branch set `errorStatus`,
        // surface it to the user — they'll see why the flow failed instead
        // of being returned to a silent empty page.
        if (errorStatus === null) {
          ctx?.setGenerationStatus(null);
        } else {
          ctx?.setGenerationStatus(errorStatus);
        }
      }
    }
  }, [
    enabled,
    editMode,
    ctx,
    entityTypeNow,
    pathname,
    pageConfig,
    tabContent,
    dispatch,
    activeFilterMap,
    applyAiSearchFilters,
    applyScreenerAiSearchFilters,
    queue,
    claim,
    complete,
    pruneExpired,
    publishLastAppliedFilterContext,
    prefsTaxonomyByEntity,
  ]);

  return null;
}
